import { Box, Button, DataTable, EmptyState, LoadError, NoResults } from "@dehub/theme";
import type { DataTableColumn } from "@dehub/theme";
import { WarehouseIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import bz from "@/features/business/business.module.css";
import type { IWarehouseListRow } from "@/models/Warehouse";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";

export const WAREHOUSE_PAGE_SIZE = 10;

type Props = Readonly<{
  rows: IWarehouseListRow[];
  columns: DataTableColumn<IWarehouseListRow>[];
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  /** A search or a status filter hides rows: shows "no results" instead of "no warehouses yet". */
  filtered: boolean;
  onClearFilters: () => void;
  page: number;
  onPageChange: (page: number) => void;
  onAdd: () => void;
  /** Why adding is not possible (permission code); null when it is. */
  addReason: ReactNode;
}>;

export function WarehouseTable({ rows, columns, loading, loadError, onRetry, filtered, onClearFilters, page, onPageChange, onAdd, addReason }: Props) {
  const { t } = useTranslation("warehouse");
  const { t: tCommon } = useTranslation("common");

  const emptyState = loadError ? (
    <LoadError title={t("list.loadErrorTitle")} message={loadError} retryLabel={tCommon("actions.retry")} onRetry={onRetry} />
  ) : filtered ? (
    <NoResults title={t("list.noResultsTitle")} description={t("list.noResultsText")} onClear={onClearFilters} clearLabel={t("list.clearFilters")} />
  ) : (
    <EmptyState
      icon={<WarehouseIcon size={24} aria-hidden="true" />}
      title={t("list.emptyTitle")}
      description={t("list.emptyText")}
      action={
        <ReasonTooltip reason={addReason}>
          <Button variant="default" onClick={onAdd}>
            {t("addBtn")}
          </Button>
        </ReasonTooltip>
      }
    />
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / WAREHOUSE_PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const from = (current - 1) * WAREHOUSE_PAGE_SIZE + 1;
  const pageRows = rows.slice((current - 1) * WAREHOUSE_PAGE_SIZE, current * WAREHOUSE_PAGE_SIZE);

  return (
    <Box className={bz.tableWrap}>
      <DataTable<IWarehouseListRow>
        data={pageRows}
        rowKey={(r) => r.id}
        cardPadding={0}
        columns={columns}
        loading={loading}
        emptyState={emptyState}
        pagination={{
          page: current,
          total: totalPages,
          onChange: onPageChange,
          label:
            rows.length > 0
              ? `${from}-${Math.min(current * WAREHOUSE_PAGE_SIZE, rows.length)} / ${rows.length} ${t("list.records")}`
              : `0 ${t("list.records")}`,
        }}
        startIndex={(current - 1) * WAREHOUSE_PAGE_SIZE}
        tableProps={{ striped: false, highlightOnHover: true, verticalSpacing: "sm", stickyHeader: false }}
      />
    </Box>
  );
}
